import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetPreviousBillingCycleQuery } from 'store/queries/bolt-api/contracts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import UsageCard from '../usage-card/UsageCard.tsx'
import { Card } from '@boltenergy-be/design-system'
import styles from './PreviousBillingCycle.module.scss'
import useWindowSize from 'hooks/useWindowSize.tsx'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import BillshockError from 'pages/app/billing/billing-cycles/components/billshock-error/BillshockError.tsx'
import PreviousBillingCycleChart from 'components/charts/billing-cycle/previous/PreviousBillingCycleChart.tsx'
import Heading from '../heading/Heading.tsx'
import { routes } from 'types/routes.ts'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'

const PreviousBillingCycle = () => {
  // URL Param
  const { id } = useParams<{ id: string }>()

  // i18n
  const { t } = useTranslation('billing')

  // Window size
  const { isTablet } = useWindowSize()

  // Redux
  const { selected } = useStoreSelector((store) => store.contact)
  const serviceContracts = useStoreSelector(selectCurrentServiceContracts)

  // Access rights
  const { showContent } = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const inaccesible = !selected.billingContract || !showContent.previous

  // Redux queries
  const { data, isLoading, error } = useGetPreviousBillingCycleQuery(
    {
      billingContractId: selected.billingContract,
      billingPeriodId: id!
    },
    { skip: !id || inaccesible }
  )

  return (
    <PageLayout
      backButtonUrl={routes.BILLING_CYCLES}
      seoTitle={t(($) => $.billingCycles.previous.title)}
      title={<Heading {...{ isLoading }} previousBillingCycle={data || undefined} electricityContract={serviceContracts.electricity} />}
      classes={{ body: styles.layout, header: styles.header }}
    >
      {/* USAGE CARD*/}
      <UsageCard
        title={t(($) => $.billingCycle.usage.title.previous)}
        electricity={data?.fuel?.electricity ? data?.usage.electricity?.consumption : undefined}
        injection={data?.usage.electricity?.injection || undefined}
        gas={data?.fuel?.gas ? data?.usage.gas : undefined}
        canAddMeterReadings={false}
        {...{ isLoading }}
      />

      {/* CHART */}
      <Card as="section" className={styles['chart-card']}>
        {isLoading ? (
          <LoadingSkeleton>
            <LoadingSkeleton.Rectangle height={56} />
            <LoadingSkeleton.Rectangle aspectRatio={isTablet ? '1.5 / 1' : '2 / 1'} />
            <div className="flex-col gap-200">
              <LoadingSkeleton.Rectangle height={20} />
              <LoadingSkeleton.Rectangle height={20} />
              <LoadingSkeleton.Rectangle height={20} />
            </div>
          </LoadingSkeleton>
        ) : error ? (
          <BillshockError error={error} />
        ) : (
          data && <PreviousBillingCycleChart {...{ isTablet, data }} />
        )}

        {/*<Button
          className={styles['consumption-button']}
          onClick={() =>
            navigate({
              pathname: routes.CONSUMPTION_ELECTRICITY, // TODO: determine correct page (gas/el) based on active view
              search: `cycle=${period.id}&type=${getPeriodType(data[period.id])}`
            })
          }
        >
          {t(($) => $.billingCycle.chart.checkPeriodConsumptionBtn)}
        </Button>*/}
      </Card>
    </PageLayout>
  )
}

export default PreviousBillingCycle
